/**
 * InsightsPage (Contestant_Insights_Page)
 *
 * Contestant-facing self-insights page. Shows the signed-in contestant's
 * season-scoped metrics, their rank, and a PII-safe peer leaderboard with
 * sparklines.
 *
 * Requirements: 11.1 - 11.15
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import {
  getContestantSeasonInsights,
  listSeasonPeerLeaderboard,
} from '@/graphql/queries';
import { AlertTriangle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import CrownLoader from '@/components/ui/CrownLoader';
import { DataTable } from '@/components/ui/data-table';
import { SeasonGrowthSparkline } from '@/components/charts/SeasonGrowthSparkline';
import { useAuth } from '@/hooks/useAuth';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import {
  USE_MOCK_SPOTLIGHT,
  MOCK_CONTESTANT_INSIGHTS,
  MOCK_PEER_LEADERBOARD,
} from '@/lib/spotlight/mockData';

const client = generateClient();

// ─── Types ──────────────────────────────────────────────────────────────────

interface ContestantInsights {
  cognitoSub: string;
  competitionYear: number;
  instagramUsername: string;
  followersGained: number;
  postsMadeThisSeason: number;
  reachThisSeason: number;
  impressionsThisSeason: number;
  baselineCapturedAt: string;
  lastSnapshotAt: string | null;
  rankByFollowersGained: number;
  totalContestants: number;
}

interface SparklinePoint {
  snapshotDate: string;
  value: number;
}

interface PeerEntry {
  rank: number;
  fullName: string;
  instagramUsername: string;
  followersGained: number;
  postsMadeThisSeason: number;
  reachThisSeason: number;
  impressionsThisSeason: number;
  sparklinePoints: SparklinePoint[];
}

type SortColumn =
  | 'followersGained'
  | 'postsMadeThisSeason'
  | 'reachThisSeason'
  | 'impressionsThisSeason';
type SortDirection = 'asc' | 'desc';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Stat card with group/tip tooltip ────────────────────────────────────

function StatCard({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: number;
  tooltip: string;
}) {
  return (
    <div className="bg-white rounded-card shadow-subtle border border-gold-100 p-5 relative">
      <div className="flex items-center justify-between">
        <p className="text-eyebrow-sm font-extrabold text-gold-400">
          {label}
        </p>
        <span className="relative group/tip">
          <Info className="w-3 h-3 text-gold-400 cursor-help" aria-hidden />
          <span
            className="absolute bottom-full right-0 mb-2 hidden group-hover/tip:block w-64 bg-gold-950 text-gold-50 text-[10px] font-sans leading-relaxed px-3 py-2 rounded shadow-raised z-50 pointer-events-none"
          >
            {tooltip}
          </span>
        </span>
      </div>
      <p className="font-display text-3xl text-gold-950 mt-2 tabular-nums">
        {formatNumber(value)}
      </p>
    </div>
  );
}

// ─── Sort indicator ────────────────────────────────────────────────────────

function SortIndicator({
  column,
  activeColumn,
  direction,
}: {
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
}) {
  if (column !== activeColumn) {
    return <ChevronDown className="w-3 h-3 text-gold-300 ml-1 inline-block" />;
  }
  return direction === 'desc' ? (
    <ChevronDown className="w-3 h-3 text-gold-700 ml-1 inline-block" />
  ) : (
    <ChevronUp className="w-3 h-3 text-gold-700 ml-1 inline-block" />
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const METRIC_COLUMNS: { key: SortColumn; label: string }[] = [
  { key: 'followersGained', label: 'Followers' },
  { key: 'postsMadeThisSeason', label: 'Posts' },
  { key: 'reachThisSeason', label: 'Reach' },
  { key: 'impressionsThisSeason', label: 'Impressions' },
];

export function InsightsPage() {
  const { user } = useAuth();
  // Season SSOT: operative competition year from the active-season seam.
  const { operativeYear: competitionYear } = useActiveSeason();

  const [insights, setInsights] = useState<ContestantInsights | null>(null);
  const [peers, setPeers] = useState<PeerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('followersGained');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Dev-mock short-circuit — bypass AppSync when USE_MOCK_SPOTLIGHT is on.
    if (import.meta.env.DEV && USE_MOCK_SPOTLIGHT) {
      setInsights(MOCK_CONTESTANT_INSIGHTS as ContestantInsights);
      setPeers(MOCK_PEER_LEADERBOARD.items as PeerEntry[]);
      setLoading(false);
      return;
    }

    try {
      const insightsRes = (await client.graphql({
        query: getContestantSeasonInsights,
        variables: { competitionYear },
        authMode: 'userPool',
      })) as GraphQLResult<{ getContestantSeasonInsights: ContestantInsights | null }>;

      const me = insightsRes.data?.getContestantSeasonInsights ?? null;
      setInsights(me);

      if (me) {
        const peersRes = (await client.graphql({
          query: listSeasonPeerLeaderboard,
          variables: { competitionYear, sortBy: 'followersGained' },
          authMode: 'userPool',
        })) as GraphQLResult<{
          listSeasonPeerLeaderboard: { items: PeerEntry[]; nextToken: string | null };
        }>;
        setPeers(peersRes.data?.listSeasonPeerLeaderboard?.items ?? []);
      } else {
        setPeers([]);
      }
    } catch (err) {
      const msg =
        (err as Error)?.message ??
        'Unable to load your insights. Try again in a moment.';
      setError(msg);
      setInsights(null);
      setPeers([]);
    } finally {
      setLoading(false);
    }
  }, [competitionYear]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedPeers = useMemo(() => {
    const sorted = [...peers].sort((a, b) => {
      const va = a[sortColumn] as number;
      const vb = b[sortColumn] as number;
      if (vb !== va) return sortDirection === 'desc' ? vb - va : va - vb;
      return a.instagramUsername.localeCompare(b.instagramUsername);
    });
    return sorted.map((row, idx) => ({ ...row, rank: idx + 1 }));
  }, [peers, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (column === sortColumn) {
        setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortColumn(column);
        setSortDirection('desc');
      }
    },
    [sortColumn],
  );

  if (loading) {
    return (
      <div data-testid="insights-page">
        <CrownLoader fullPage message="Loading insights…" />
      </div>
    );
  }

  // Error state (inline red panel, never a native alert)
  if (error) {
    return (
      <div data-testid="insights-page" className="space-y-6">
        <h1 className="font-display text-3xl text-gold-950">Insights</h1>
        <div
          className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 flex items-center gap-3 text-red-800 text-sm"
          role="alert"
          data-testid="insights-error"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // No-baseline state (insights === null → contestant isn't approved yet or is exempt)
  if (!insights) {
    return (
      <div data-testid="insights-page" className="space-y-6">
        <h1 className="font-display text-3xl text-gold-950">Insights</h1>
        <div
          className="bg-white rounded-card shadow-subtle border border-gold-100 p-8 text-center"
          data-testid="insights-no-baseline"
        >
          <p className="font-display text-lg text-gold-700">
            Your insights will appear here once your application is approved for
            the current season.
          </p>
        </div>
      </div>
    );
  }

  // Main view: rank header + 4 stat cards + peer leaderboard
  const baselineDateLabel = `Baseline captured ${formatDate(
    insights.baselineCapturedAt,
  )}`;

  const myInstagramUsername = insights.instagramUsername;

  return (
    <div data-testid="insights-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-gold-950">Insights</h1>
          <p
            className="text-eyebrow-sm font-extrabold text-gold-400 mt-1"
            data-testid="baseline-date-label"
          >
            {baselineDateLabel}
          </p>
        </div>
        <p
          className="font-sans text-xs uppercase tracking-[0.2em] text-gold-950"
          data-testid="rank-label"
        >
          Rank {insights.rankByFollowersGained} of {insights.totalContestants}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Followers Gained"
          value={insights.followersGained}
          tooltip={`New followers you've gained since your application was approved on ${formatDate(
            insights.baselineCapturedAt,
          )}. We took a snapshot of your follower count on that day and track growth from there.`}
        />
        <StatCard
          label="Posts Made"
          value={insights.postsMadeThisSeason}
          tooltip={`Posts you've shared since ${formatDate(
            insights.baselineCapturedAt,
          )}.`}
        />
        <StatCard
          label="Reach"
          value={insights.reachThisSeason}
          tooltip={`Total reach of your posts since ${formatDate(
            insights.baselineCapturedAt,
          )}.`}
        />
        <StatCard
          label="Impressions"
          value={insights.impressionsThisSeason}
          tooltip={`Total impressions of your posts since ${formatDate(
            insights.baselineCapturedAt,
          )}.`}
        />
      </div>

      {/* Peer leaderboard — PR (m.10) DataTable migration. */}
      <DataTable
        ariaLabel="Peer leaderboard"
        data-table-testid="peer-leaderboard-table"
        rowKey={(p) => `${p.instagramUsername}-${p.rank}`}
        rows={sortedPeers}
        rowTestId={(p) => `peer-row-${p.instagramUsername}`}
        rowClassName={(p) => {
          const isMe =
            p.instagramUsername === myInstagramUsername ||
            (user?.fullName &&
              p.fullName.trim() === user.fullName.trim() &&
              p.instagramUsername === myInstagramUsername);
          return isMe ? 'bg-muted/40 border-l-[3px] border-l-primary' : undefined;
        }}
        rowDataAttrs={(p) => {
          const isMe =
            p.instagramUsername === myInstagramUsername ||
            (user?.fullName &&
              p.fullName.trim() === user.fullName.trim() &&
              p.instagramUsername === myInstagramUsername);
          return { 'data-is-me': isMe ? 'true' : 'false' };
        }}
        minWidth={920}
        columns={[
          {
            id: 'rank',
            header: '#',
            width: '48px',
            renderCell: (p) => (
              <span className="font-display text-sm text-muted-foreground font-bold">
                {p.rank}
              </span>
            ),
          },
          {
            id: 'name',
            header: 'Name',
            renderCell: (p) => (
              <span className="font-display text-sm text-foreground font-semibold">
                {p.fullName || '—'}
              </span>
            ),
          },
          {
            id: 'handle',
            header: 'Handle',
            renderCell: (p) => (
              <span className="text-sm text-muted-foreground">
                @{p.instagramUsername}
              </span>
            ),
            hideOnMobile: true,
          },
          {
            id: 'growth',
            header: 'Growth',
            renderCell: (p) => (
              <SeasonGrowthSparkline
                key={p.instagramUsername}
                points={p.sparklinePoints}
                ariaLabel={`Growth sparkline for ${p.fullName || p.instagramUsername}`}
              />
            ),
            hideOnMobile: true,
          },
          ...METRIC_COLUMNS.map((col) => ({
            id: col.key,
            align: 'right' as const,
            hideOnMobile: true,
            header: (
              <button
                type="button"
                onClick={() => handleSort(col.key)}
                data-testid={`peer-sort-${col.key}`}
                className="inline-flex items-center justify-end gap-1 cursor-pointer hover:text-foreground transition-colors bg-transparent border-none text-eyebrow-xs text-muted-foreground touch-target"
              >
                {col.label}
                <SortIndicator
                  column={col.key}
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </button>
            ),
            renderCell: (p: typeof sortedPeers[number]) => (
              <span className="font-sans text-sm text-foreground tabular-nums">
                {formatNumber(p[col.key as keyof typeof p] as number)}
              </span>
            ),
          })),
        ]}
      />
    </div>
  );
}

export default InsightsPage;
